import { Alert, AlertDescription } from '@1trading/react';
import type { ColumnDef } from '@tanstack/react-table';
import { DateCell } from '@/components/data-table/cells';
import { DataTable } from '@/components/data-table/data-table';
import { type FundsTransaction, TRANSACTIONS_MAX_PAGE_SIZE } from '@/services/api/fiu-utr';

const PAGE_SIZE = 25;

// Raw values stay on the row, formatting lives in the cells
const columns: ColumnDef<FundsTransaction, unknown>[] = [
  {
    accessorKey: 'transaction_id',
    header: 'Transaction ID',
    enableSorting: false,
    cell: ({ row }) => <span className="font-mono text-xs">{row.original.transaction_id}</span>,
  },
  {
    accessorKey: 'user_requested_at',
    header: 'Requested At',
    cell: ({ row }) => <DateCell value={row.original.user_requested_at} />,
  },
  { accessorKey: 'transfer_method_type', header: 'Method' },
  { accessorKey: 'direction', header: 'Direction' },
  { accessorKey: 'transfer_status', header: 'Status' },
  {
    accessorKey: 'amount',
    header: 'Amount',
    cell: ({ row }) => (
      <span className="whitespace-nowrap tabular-nums">
        {row.original.amount} {row.original.currency}
      </span>
    ),
  },
];

interface TransactionsSectionProps {
  transactions: FundsTransaction[];
  /** The funds API returned a cursor: older transactions are not listed */
  hasMore: boolean;
  isLoading: boolean;
  /** changes on every completed search — remounts the table so the selection clears */
  tableKey: string;
  onSelectionChange: (rows: FundsTransaction[]) => void;
}

export const TransactionsSection = ({ transactions, hasMore, isLoading, tableKey, onSelectionChange }: TransactionsSectionProps) => (
  <section className="flex flex-col gap-3">
    <h2 className="text-lg font-semibold">1. Select transactions to include</h2>
    <p className="text-sm text-muted-foreground">Only deposits and withdrawals are listed. Fees, Instant Trade and other internal transfers are not reportable.</p>
    {hasMore ? (
      // Deliberate: the 100-row cap is kept but no longer silent — the operator is
      // told the list is incomplete instead of unknowingly filing from a truncated view.
      <Alert className="max-w-[900px] border-amber-500/40 bg-amber-500/10 text-amber-600 dark:text-amber-400">
        <AlertDescription>Only the first {TRANSACTIONS_MAX_PAGE_SIZE} transactions are shown — this user has more. Older transactions cannot be selected here.</AlertDescription>
      </Alert>
    ) : null}
    <DataTable
      columns={columns}
      data={transactions}
      emptyMessage="No transactions found for this PID."
      enableRowSelection
      getRowId={(row) => row.transaction_id}
      isLoading={isLoading}
      key={tableKey}
      onSelectionChange={onSelectionChange}
      pageSize={PAGE_SIZE}
    />
  </section>
);
TransactionsSection.displayName = 'TransactionsSection';
